import createClient, { type Middleware } from "openapi-fetch";
import { signOut, token } from "@/shared/auth/session";
import { toast } from "@/shared/components/Toast";
import type { paths } from "./schema";

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

const authMiddleware: Middleware = {
  onRequest({ request }) {
    const current = token();
    if (current) request.headers.set("Authorization", `Bearer ${current}`);
    return request;
  },
  onResponse({ response }) {
    // A 401 means the token no longer identifies anybody -- expired, revoked,
    // or issued to an account since deactivated. Dropping it here lets the
    // route guard fall through to the login page.
    if (response.status === 401) signOut();
    // A 403 is the opposite: the session is fine, this particular thing is not
    // allowed. Signing out over it would be a confusing way to say so.
    if (response.status === 403) toast("You do not have permission to do that", "error");
    return response;
  },
};

/**
 * Typed client for the Go API. Paths, params, and payloads are checked against
 * `schema.d.ts`, which is generated from the server's own OpenAPI document --
 * run `npm run gen:api` after changing a handler.
 */
export const api = createClient<paths>();
api.use(authMiddleware);

/**
 * Narrows an openapi-fetch result to its data, throwing on failure. Use it when
 * a caller wants an exception; read `{ data, error }` directly when it wants to
 * branch.
 */
export async function unwrap<T>(result: {
  data?: T;
  error?: unknown;
  response: Response;
}): Promise<T> {
  if (result.error !== undefined || !result.response.ok) {
    throw new ApiError(
      describe(result.error, result.response),
      result.response.status,
    );
  }
  return result.data as T;
}

// Fuego reports validation and routing failures as RFC 7807 problem details,
// while the hand-written auth handler returns {"error": "..."}. Accept either.
function describe(error: unknown, response: Response): string {
  if (typeof error === "string" && error) return error;
  if (error && typeof error === "object") {
    const body = error as Record<string, unknown>;
    for (const key of ["detail", "title", "error", "message"]) {
      const value = body[key];
      if (typeof value === "string" && value) return value;
    }
  }
  return `Request failed with status ${response.status}`;
}
